import { getRoot, Instance, types } from 'mobx-state-tree';
import { PageModel, RootType } from '../../../internal';
import {
  QuizApiItem,
  QuizAnswerApiItem,
  QuizQuestionApiItem,
  quizImageUrl,
} from '../../../utils/api/QuizApi';

export const OdgovoriModel = types
  .compose(
    PageModel,
    types.model({
      quizzes: types.frozen<QuizApiItem[]>([]),
      selectedQuizId: types.maybe(types.number),
      questions: types.frozen<QuizQuestionApiItem[]>([]),
      selectedQuestionId: types.maybe(types.number),
      answers: types.frozen<QuizAnswerApiItem[]>([]),
    })
  )
  .views((self) => ({
    get root(): RootType {
      return getRoot(self);
    },
    get selectedQuestion(): QuizQuestionApiItem | undefined {
      return self.questions.find((q) => q.id === self.selectedQuestionId);
    },
    get selectedQuestionImageUrl(): string {
      const question = self.questions.find(
        (q) => q.id === self.selectedQuestionId
      );
      return quizImageUrl(question?.image);
    },
  }))
  .actions((self) => ({
    setQuizzes(quizzes: QuizApiItem[]) {
      self.quizzes = quizzes;
    },
    setSelectedQuizId(id: number) {
      self.selectedQuizId = id;
    },
    setSelectedQuestionId(id: number) {
      self.selectedQuestionId = id;
    },
    setQuestions(questions: QuizQuestionApiItem[]) {
      self.questions = questions;
      self.selectedQuestionId = undefined;
      self.answers = [];
    },
    setQuestionsOnly(questions: QuizQuestionApiItem[]) {
      self.questions = questions;
    },
    setAnswers(answers: QuizAnswerApiItem[]) {
      self.answers = answers;
    },
    async beforePageEnter() {
      const quizzes = await self.root.api.quiz.getAll();
      this.setQuizzes(quizzes.slice().reverse());

      const { quizId, questionId } = self.root.router.queryParams;
      if (quizId) {
        this.setSelectedQuizId(Number(quizId));
        const questions = await self.root.api.quiz.getQuestions(Number(quizId));
        this.setQuestionsOnly(questions);

        if (questionId) {
          this.setSelectedQuestionId(Number(questionId));
          const answers = await self.root.api.quiz.getAnswers(
            Number(quizId),
            Number(questionId)
          );
          this.setAnswers(answers);
        }
      }
    },
    async selectQuiz(quizId: number) {
      self.selectedQuizId = quizId;
      this.setQuestions([]);
      const questions = await self.root.api.quiz.getQuestions(quizId);
      this.setQuestions(questions);

      const view = self.root.router.views.get('Odgovori');
      self.root.router.history.replace(
        view.formatUrl({}, { quizId: String(quizId) })
      );
    },
    async selectQuestion(questionId: number) {
      self.selectedQuestionId = questionId;
      this.setAnswers([]);
      if (self.selectedQuizId == null) return;
      const answers = await self.root.api.quiz.getAnswers(
        self.selectedQuizId,
        questionId
      );
      this.setAnswers(answers);

      const view = self.root.router.views.get('Odgovori');
      self.root.router.history.replace(
        view.formatUrl(
          {},
          {
            quizId: String(self.selectedQuizId),
            questionId: String(questionId),
          }
        )
      );
    },
    async deleteAnswer(answerId: number) {
      const quizId = self.selectedQuizId;
      const questionId = self.selectedQuestionId;
      if (quizId == null || questionId == null) return;
      try {
        await self.root.api.quiz.deleteAnswer(quizId, questionId, answerId);
        this.setAnswers(self.answers.filter((a) => a.id !== answerId));
      } catch (err: any) {
        const raw = err?.response?.data?.message;
        const message =
          raw === 'Cannot delete answer if the question is active'
            ? 'Nije moguće obrisati odgovor dok je pitanje aktivno.'
            : raw ?? 'Došlo je do greške pri brisanju odgovora.';
        self.root.showFailure(message, null);
      }
    },
  }))
  .named('OdgovoriModel');

export type OdgovoriModelType = Instance<typeof OdgovoriModel>;
